import { useIonPopover, useIonToast } from '@ionic/react';
import { BUSINESS_CHECKIN_TOPIC, Business, EmptyBusinessCheckIn, EmptyBusinessPost, User } from 'model';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { useDeleteBusinessMutation } from '../api/BusinessApi';
import { useCreatePostMutation } from '../api/PostApi';
import { useStarBusinessMutation } from '../api/StarredBusinessApi';
import { useBusinessIsStarred } from '../hooks/useBusinessIsStarred';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useShare } from '../hooks/useShare';
import { useUserHasRole } from '../hooks/useUserHasRole';
import { BusinessDetailBody } from '../pages/BusinessDetailBody';
import { RealUserOnly } from '../pages/admin/RealUserOnly';
import { createCheckInTitle } from '../util/checkIn';
import { isMutationError } from '../util/error';
import { DeleteItem } from './DeleteItem';
import { IconButton } from './IconButton';
import { IconEdit, IconLocation, IconMoreHorizontal, IconPen, IconShare, IconStar, IconStarSolid } from './QuirkIcons';
import { TruncatedSummary } from './TruncatedSummary';
// eslint-disable-next-line css-modules/no-unused-class
import sharedStyles from '../styles/Shared.module.css';
import { getBackendURL, useBackendDomain } from '../hooks/useBackendDomain.js';
export interface BusinessContextMenuProps {
	business: Business;
	context: 'detail' | 'list';
}

type BusinessPopupProps = {
	isOwner: boolean;
	isAdmin: boolean;
	isSubmitter: boolean;
	handleEditClick: () => void;
	context: string;
	business: Business;
	hide: (data?: any, role?: string | undefined) => void;
	isPending: boolean;
	handleCreatePostClick: () => Promise<void>;
	isCreatingPost: boolean;
	isUpdating: boolean;
	handleStarClick: () => void;
	handleShareClick: () => void;
	user: User;
};
const BusinessPopup: React.FC<BusinessPopupProps> = ({
	isOwner,
	isAdmin,
	isSubmitter,
	handleEditClick,
	context,
	business,
	hide,
	isPending,
	handleCreatePostClick,
	isCreatingPost,
	isUpdating,
	handleStarClick,
	handleShareClick,
	user
}) => {
	const isStarred = useBusinessIsStarred(business._id, user._id);

	return (
		<div className={sharedStyles.popMenu}>
			{(isOwner || isAdmin || isSubmitter) && (
				<IconButton icon={<IconEdit size={24} />} onClick={handleEditClick} label="Edit" />
			)}
			{context === 'list' && (isAdmin || isSubmitter) && (
				<>
					<DeleteItem
						item={business}
						hideTooltip={() => hide()}
						actionLabel="Delete Business"
						dismissLabel="Cancel"
						mutation={useDeleteBusinessMutation}
						modalContent={
							<>
								<h4 style={{ marginBottom: '40px' }}>Are you sure you want to delete this business?</h4>
								<TruncatedSummary maxHeight={400}>
									<BusinessDetailBody business={business} />
								</TruncatedSummary>
							</>
						}
					/>
				</>
			)}
			{context === 'detail' && !isPending && (
				<>
					<IconButton
						icon={isOwner ? <IconPen size={24} /> : <IconLocation />}
						label={isOwner ? 'Create post' : 'Check in'}
						onClick={handleCreatePostClick}
						disabled={isCreatingPost}
					/>
					<RealUserOnly>
						<IconButton
							iconColor="#ffc409"
							icon={isStarred ? <IconStarSolid size={24} /> : <IconStar size={24} />}
							label={isStarred ? 'Unfavorite' : 'Favorite'}
							disabled={isUpdating || isStarred === undefined}
							onClick={handleStarClick}
						/>
					</RealUserOnly>
					<IconButton icon={<IconShare size={24} />} onClick={handleShareClick} label="Share" />
				</>
			)}
		</div>
	);
};

export const BusinessContextMenu: React.FC<BusinessContextMenuProps> = ({
	business,
	context
}: BusinessContextMenuProps) => {
	const [domain] = useBackendDomain();
	const { handleShare } = useShare({
		message: getBackendURL('/api/sharing/' + business?._id, domain || window.location.origin),
		header: 'Share this business'
	});
	const [toggleStar, { isLoading: isUpdating }] = useStarBusinessMutation();
	const user = useCurrentUser();
	const isAdmin = useUserHasRole('admin');
	const isOwner = business.owner === user._id && business.claimStatus === 'claimed';
	const isSubmitter =
		business.submittedBy === user._id &&
		business.submissionStatus !== '' &&
		business.submissionStatus !== 'approved';
	const isPending = business.submissionStatus === 'pending';
	const history = useHistory();
	const handleEditClick = () => {
		hide();
		history.push(`/main/bus/edit/${business._id}`);
	};
	const handleShareClick = () => {
		hide();
		handleShare();
	};
	const handleStarClick = () => {
		hide();
		toggleStar({ businessId: business._id, userId: user._id });
	};
	const [showToast] = useIonToast();
	const [createPost, { isLoading: isCreatingPost }] = useCreatePostMutation();
	const handleCreatePostClick = async () => {
		hide();
		const result = await createPost({
			post: {
				...(isOwner ? EmptyBusinessPost : EmptyBusinessCheckIn),
				businessId: business._id,
				title: isOwner ? '' : createCheckInTitle(user, business),
				topic: isOwner ? '' : BUSINESS_CHECKIN_TOPIC,
				author: user._id
			}
		});
		if (isMutationError(result)) {
			showToast({
				duration: 1500,
				color: 'danger',
				position: 'top',
				message: 'There was an error creating the post'
			});
		} else {
			history.push(`/main/latest/bus/${business._id}/edit/${result.data._id}`);
		}
	};
	const [show, hide] = useIonPopover(
		props => {
			return <BusinessPopup {...props} hide={hide} />;
		},
		{
			isOwner,
			isAdmin,
			isSubmitter,
			handleEditClick,
			context,
			business,
			isPending,
			handleCreatePostClick,
			isCreatingPost,
			isUpdating,
			handleStarClick,
			handleShareClick,
			user
		}
	);
	return (
		<div className={sharedStyles.icon}>
			<IconButton icon={<IconMoreHorizontal />} onClick={event => show({ event })} />
		</div>
	);
};
